import React, { useState, useEffect } from 'react';
import { subscribeToCollection, saveDocument, deleteDocument } from '../firebase';
import AssignmentBox from './AssignmentBox';

export default function CarAssignments({ users = [] }) {
  const [cars, setCars] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Which car is currently being edited (by id)
  const [editingCarId, setEditingCarId] = useState(null);
  // Edit form state
  const [editDesc, setEditDesc] = useState('');
  const [editCapacity, setEditCapacity] = useState('');
  const [editDrivers, setEditDrivers] = useState([]);

  // Add Car form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCarName, setNewCarName] = useState('');
  const [newCarDesc, setNewCarDesc] = useState('');
  const [newCarCapacity, setNewCarCapacity] = useState('');
  const [newCarDrivers, setNewCarDrivers] = useState([]);

  useEffect(() => {
    const unsubscribe = subscribeToCollection('cars', (data) => {
      const sorted = [...data].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCars(sorted);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const getDriverList = (car) => Array.isArray(car.drivers) ? car.drivers : [];

  // ── Per-card edit ─────────────────────────────────────────────────────────
  const handleStartEdit = (car) => {
    setEditingCarId(car.id);
    setEditDesc(car.description || '');
    setEditCapacity(car.capacity != null ? String(car.capacity) : '');
    setEditDrivers(getDriverList(car));
  };

  const handleSaveEdit = async (car) => {
    await saveDocument('cars', car.id, {
      ...car,
      description: editDesc.trim(),
      capacity: editCapacity ? parseInt(editCapacity, 10) : null,
      drivers: editDrivers,
    });
    setEditingCarId(null);
  };

  const handleCancelEdit = () => setEditingCarId(null);

  const handleDeleteCar = async (car) => {
    if (!window.confirm(`Delete "${car.name}"? This cannot be undone.`)) return;
    await deleteDocument('cars', car.id);
    if (editingCarId === car.id) setEditingCarId(null);
  };

  // ── Add Car form ──────────────────────────────────────────────────────────
  const handleAddCar = async () => {
    const name = newCarName.trim();
    if (!name) return;
    const id = `car_${Date.now()}`;
    await saveDocument('cars', id, {
      id,
      name,
      description: newCarDesc.trim(),
      capacity: newCarCapacity ? parseInt(newCarCapacity, 10) : null,
      drivers: newCarDrivers,
    });
    setNewCarName('');
    setNewCarDesc('');
    setNewCarCapacity('');
    setNewCarDrivers([]);
    setShowAddForm(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="tab-panel">
      {/* Page header */}
      <div className="view-title-container" style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div className="view-title">
          <h2>🚗 Car Assignments</h2>
        </div>
        <button
          className={showAddForm ? 'btn-secondary' : 'btn-primary'}
          style={{ fontSize: '0.85rem', padding: '8px 18px', borderRadius: '20px' }}
          onClick={() => { setShowAddForm(v => !v); setEditingCarId(null); }}
        >
          {showAddForm ? '✕ Cancel' : '+ Add Car'}
        </button>
      </div>

      {/* ── Add Car Form ── */}
      {showAddForm && (
        <div className="glass-panel" style={{ padding: '20px', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '14px', borderLeft: '3px solid var(--color-primary)' }}>
          <h4 style={{ margin: 0, fontSize: '1rem', color: 'var(--color-text-main)' }}>New Car</h4>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ flex: '2', minWidth: '160px' }}>
              <label style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', display: 'block', marginBottom: '4px' }}>
                Car Name / Nickname <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input
                type="text"
                className="input-field"
                placeholder="e.g. Millennium Falcon"
                value={newCarName}
                onChange={e => setNewCarName(e.target.value)}
                style={{ width: '100%', fontSize: '0.85rem' }}
              />
            </div>
            <div style={{ flex: '2', minWidth: '140px' }}>
              <label style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', display: 'block', marginBottom: '4px' }}>Notes (optional)</label>
              <input
                type="text"
                className="input-field"
                placeholder="e.g. car seats"
                value={newCarDesc}
                onChange={e => setNewCarDesc(e.target.value)}
                style={{ width: '100%', fontSize: '0.85rem' }}
              />
            </div>
            <div style={{ width: '80px', flexShrink: 0 }}>
              <label style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', display: 'block', marginBottom: '4px' }}>Seats</label>
              <input
                type="number"
                min="1"
                max="20"
                className="input-field"
                placeholder="5"
                value={newCarCapacity}
                onChange={e => setNewCarCapacity(e.target.value)}
                style={{ width: '100%', fontSize: '0.85rem' }}
              />
            </div>
          </div>

          <AssignmentBox
            label="Drivers (optional)"
            selectedIds={newCarDrivers}
            users={users}
            isEditing={true}
            onAdd={(dId) => setNewCarDrivers(prev => [...prev, dId])}
            onRemove={(dId) => setNewCarDrivers(prev => prev.filter(d => d !== dId))}
            addPlaceholder="-- Add driver --"
            emptyPlaceholder="No drivers assigned"
          />

          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn-primary" style={{ fontSize: '0.8rem', padding: '7px 20px' }} onClick={handleAddCar}>Create Car</button>
            <button className="btn-secondary" style={{ fontSize: '0.8rem', padding: '7px 14px' }} onClick={() => setShowAddForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Car Grid ── */}
      <div className="room-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
        {isLoading ? (
          <div className="loading-container" style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <div className="loading-chip"><span className="spinner"></span><span>Loading Cars...</span></div>
          </div>
        ) : cars.length === 0 ? (
          <div className="glass-panel" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)', gridColumn: '1 / -1' }}>
            No cars added yet. Click <strong>+ Add Car</strong> to get started.
          </div>
        ) : (
          cars.map(car => {
            const isEditing = editingCarId === car.id;
            const drivers = getDriverList(car);

            if (isEditing) {
              // ── Edit mode ─────────────────────────────────────────────────
              return (
                <div key={car.id} className="room-card" style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px', gridColumn: 'span 1' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: '700', margin: 0, color: 'var(--color-text-main)' }}>{car.name}</h3>
                    <button
                      className="btn-inline-delete-cross"
                      style={{ lineHeight: 1, fontSize: '1rem' }}
                      title="Delete this car"
                      onClick={() => handleDeleteCar(car)}
                    >×</button>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1', minWidth: '120px' }}>
                      <label style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', display: 'block', marginBottom: '3px' }}>Notes</label>
                      <input
                        type="text"
                        className="input-field"
                        value={editDesc}
                        onChange={e => setEditDesc(e.target.value)}
                        placeholder="e.g. has roof rack"
                        style={{ width: '100%', fontSize: '0.8rem', padding: '4px 8px' }}
                      />
                    </div>
                    <div style={{ width: '72px', flexShrink: 0 }}>
                      <label style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', display: 'block', marginBottom: '3px' }}>Seats</label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        className="input-field"
                        value={editCapacity}
                        onChange={e => setEditCapacity(e.target.value)}
                        placeholder="?"
                        style={{ width: '100%', fontSize: '0.8rem', padding: '4px 6px' }}
                      />
                    </div>
                  </div>

                  <AssignmentBox
                    label="Drivers"
                    selectedIds={editDrivers}
                    users={users}
                    isEditing={true}
                    onAdd={(dId) => setEditDrivers(prev => [...prev, dId])}
                    onRemove={(dId) => setEditDrivers(prev => prev.filter(d => d !== dId))}
                    addPlaceholder="-- Add driver --"
                    emptyPlaceholder="No drivers assigned"
                  />

                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      className="btn-primary"
                      style={{ fontSize: '0.78rem', padding: '5px 16px', background: 'var(--color-accent)' }}
                      onClick={() => handleSaveEdit(car)}
                    >
                      ✓ Save
                    </button>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: '0.78rem', padding: '5px 12px' }}
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              );
            }

            // ── Display mode ───────────────────────────────────────────────
            return (
              <div key={car.id} className="room-card" style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '6px', minHeight: 'auto' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h3 style={{ fontSize: '0.95rem', color: 'var(--color-text-main)', fontWeight: '700', margin: 0 }}>
                      {car.name}
                    </h3>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '3px', flexWrap: 'wrap' }}>
                      {car.description && (
                        <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>{car.description}</span>
                      )}
                      {car.capacity && (
                        <span style={{
                          fontSize: '0.68rem', fontWeight: '700',
                          color: 'var(--color-primary)',
                          background: 'rgba(2, 132, 199, 0.08)',
                          border: '1px solid rgba(2, 132, 199, 0.15)',
                          padding: '1px 7px', borderRadius: '10px'
                        }}>
                          Seats {car.capacity}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    className="btn-inline-edit"
                    style={{ fontSize: '0.72rem', padding: '2px 8px', marginLeft: '6px', flexShrink: 0 }}
                    onClick={() => handleStartEdit(car)}
                  >
                    [Edit]
                  </button>
                </div>

                {/* Drivers */}
                <AssignmentBox
                  label="Drivers"
                  selectedIds={drivers}
                  users={users}
                  isEditing={false}
                  emptyPlaceholder="No drivers assigned"
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
